import {
  array,
  boolean,
  instance,
  literal,
  minLength,
  object,
  optional,
  picklist,
  pipe,
  rawCheck,
  string,
  union,
  variant,
} from "valibot";
import FieldId from "../../fields/FieldId";
import { isEmpty, isNil } from "lodash-es";
import FieldService from "../../services/FieldService";
import { errorTranslation, requireText } from "./base";
import { fields } from "../../fields/fields";
import { getPredefinedOptions } from "../ruleUtil";
import type { RuleCondition, ValueAction } from "../../state/formVersion";
import { RuleActionKey } from "../../state/formVersion";
import { MAX_CHARACTERS_MD } from "../../constants";
import type { FieldConfig } from "../../fields/types";
import VariableId from "../../variables/VariableId";
import VariableService from "../../services/VariableService";
import { keyFromSelector } from "i18next";

const operator = () => picklist(["OR", "AND"], errorTranslation(keyFromSelector(($) => $.VALIDATION_RULES_OPERATOR)));
const conditionKey = () =>
  picklist(
    ["is", "startsWith", "endsWith", "contains", "greaterThan", "lessThan", "hasValue"],
    errorTranslation(keyFromSelector(($) => $.VALIDATION_RULES_CONDITION_INVALID_KEY)),
  );

const fieldId = (pathKey: string) =>
  pipe(
    instance(FieldId, errorTranslation(keyFromSelector(($) => $.VALIDATION_RULES_CONDITION_FIELD_REQUIRED))),
    rawCheck(({ dataset, addIssue }) => {
      if (dataset.typed && isNil(FieldService.getField(dataset.value))) {
        addIssue({
          message: errorTranslation(keyFromSelector(($) => $.VALIDATION_RULES_CONDITION_INVALID_FIELD)),
          path: [
            {
              type: "object",
              origin: "value",
              input: { fieldId: dataset.value },
              key: pathKey,
              value: { scope: "parent" },
            },
          ],
        });
      }
    }),
  );

const variableId = (pathKey: string) =>
  pipe(
    instance(VariableId, errorTranslation(keyFromSelector(($) => $.VALIDATION_RULES_CONDITION_FIELD_REQUIRED))),
    rawCheck(({ dataset, addIssue }) => {
      if (dataset.typed && isNil(VariableService.getVariable(dataset.value))) {
        addIssue({
          message: errorTranslation(keyFromSelector(($) => $.VALIDATION_RULES_CONDITION_INVALID_FIELD)),
          path: [
            {
              type: "object",
              origin: "value",
              input: { variableId: dataset.value },
              key: pathKey,
              value: { scope: "parent" },
            },
          ],
        });
      }
    }),
  );

const value = () =>
  union(
    [requireText(MAX_CHARACTERS_MD), boolean()],
    errorTranslation(keyFromSelector(($) => $.VALIDATION_RULES_CONDITION_INVALID_VALUE)),
  );

const addPredefinedOptionIssues = (
  ruleMutation: RuleCondition | ValueAction,
  addIssue: (info?: unknown) => void,
  field?: FieldConfig,
) => {
  const hasSelectState = !isNil(ruleMutation.value) && ruleMutation.key !== "hasValue";
  const hasSelectBehaviour = !isNil(field) && fields[field.type].ruleBehaviour?.conditions.variant === "select";
  if (!hasSelectState || !hasSelectBehaviour) {
    return;
  }

  if (hasSelectBehaviour && isEmpty(ruleMutation.value)) {
    return;
  }

  const options = getPredefinedOptions(field);
  if (!options.some((option) => option.value === ruleMutation.value)) {
    addIssue({
      message: errorTranslation(
        keyFromSelector(($) => $.VALIDATION_RULES_ACTION_INVALID_VALUE_REFERENCE),
        {
          reference: ruleMutation.value,
        },
      ),
      path: [{ type: "object", origin: "value", input: ruleMutation, key: "value", value: { scope: "parent" } }],
    });
  }
};

export const schema = object({
  name: optional(string()),
  operator: operator(),
  conditions: pipe(
    array(
      variant("type", [
        pipe(
          object({
            type: literal("FIELD"),
            key: conditionKey(),
            fieldId: fieldId("fieldId"),
            value: value(),
          }),
          rawCheck(({ dataset, addIssue }) => {
            if (dataset.typed) {
              const condition = dataset.value;
              const field = FieldService.getField(condition.fieldId);
              addPredefinedOptionIssues(condition, addIssue as never, field);
            }
          }),
        ),
        object({
          type: literal("FIELD_DATA_SOURCE"),
          key: conditionKey(),
          fieldId: fieldId("fieldId"),
          fieldObjectKey: string(errorTranslation(keyFromSelector(($) => $.VALIDATION_RULES_CONDITION_INVALID_COLUMN))),
          value: value(),
        }),
        object({
          type: literal("VARIABLE"),
          key: conditionKey(),
          variableId: variableId("variableId"),
          value: value(),
        }),
        object({
          type: literal("USER"),
          key: conditionKey(),
          value: value(),
        }),
        object({
          type: literal("EMPTY"),
          fieldId: fieldId("fieldId"),
        }),
      ]),
    ),
    minLength(1, errorTranslation(keyFromSelector(($) => $.VALIDATION_RULES_CONDITIONS_REQUIRED))),
  ),
  actions: pipe(
    array(
      variant(
        "key",
        [
          pipe(
            object({
              key: literal(RuleActionKey.VALUE),
              fieldId: fieldId("fieldId"),
              value: union(
                [string(), boolean()],
                errorTranslation(keyFromSelector(($) => $.VALIDATION_RULES_ACTION_INVALID_VALUE)),
              ),
            }),
            rawCheck(({ dataset, addIssue }) => {
              if (dataset.typed) {
                const action = dataset.value;
                const field = FieldService.getField(action.fieldId);
                addPredefinedOptionIssues(action, addIssue as never, field);
              }
            }),
          ),
          object({
            key: literal(RuleActionKey.REFERENCE),
            fieldId: fieldId("fieldId"),
            value: union([fieldId("value"), variableId("value")]),
          }),
          object({
            key: literal(RuleActionKey.VISIBLE),
            fieldId: fieldId("fieldId"),
          }),
          object({
            key: literal(RuleActionKey.HIDDEN),
            fieldId: fieldId("fieldId"),
          }),
        ],
        errorTranslation(keyFromSelector(($) => $.VALIDATION_RULES_ACTION_INVALID_KEY)),
      ),
    ),
    minLength(1, errorTranslation(keyFromSelector(($) => $.VALIDATION_RULES_ACTIONS_REQUIRED))),
  ),
});
